// A search-as-you-type picker over /admin/organizations (used to create, move and invoice).
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import type { OrganizationAdmin } from '../types/api';
import { Input } from './ui';

export function OrgPicker({ value, onChange, allowNone, id }: { value: OrganizationAdmin | null; onChange: (org: OrganizationAdmin | null) => void; allowNone?: boolean; id?: string }) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [options, setOptions] = useState<OrganizationAdmin[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const handle = window.setTimeout(() => {
      api.organizations({ q: q || undefined, page_size: 20 }).then((page) => {
        if (!cancelled) setOptions(page.items);
      }, () => undefined);
    }, 150);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [q, open]);

  return (
    <div className="relative">
      {value ? (
        <div className="flex items-center justify-between rounded-md border border-line-btn bg-white px-2.5 py-[6px] text-[13px]">
          <span>
            <span className="text-ink">{value.name}</span> <span className="font-mono text-[11px] text-faint">#{value.id} · {value.kind}</span>
          </span>
          <button type="button" onClick={() => onChange(null)} className="font-mono text-[10.5px] text-faint hover:text-ink">
            {t('common.change')}
          </button>
        </div>
      ) : (
        <Input id={id} placeholder={allowNone ? t('orgs.pickerNone') : t('orgs.picker')} value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => setOpen(true)} onBlur={() => window.setTimeout(() => setOpen(false), 150)} autoComplete="off" data-testid="org-picker" />
      )}
      {open && !value && options.length > 0 && (
        <ul className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-md border border-line-card bg-white py-1 shadow-sm" role="listbox">
          {options.map((org) => (
            <li key={org.id}>
              <button type="button" role="option" aria-selected={false} onMouseDown={() => onChange(org)} className="flex w-full items-center justify-between px-3 py-1.5 text-left text-[12.5px] hover:bg-hover-soft">
                <span className="text-ink">{org.name}</span>
                <span className="font-mono text-[10.5px] text-faint">
                  #{org.id} · {org.kind} · {org.member_count}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
